import { useState } from 'react';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Stack, ToggleButton, Typography } from '@mui/material';
import { SelectedCourseCard } from '@/packages/idp/components/selected-course-card';
import { ActivityType } from '@/data/idp/interfaces';
import { type RecommendedPlanActivity } from '@/data/idp/profile-based-recommendations/interfaces';
import type { Course } from '@/models/catalog/course';
import { RecommendedCoursesButton } from '@/packages/idp/components/recommended-courses-button';
import { RecommendedCoursesDialog } from '@/packages/idp/components/recommended-courses-dialog';
import { mapCatalogCourseToEmbeddedCourse } from '@/utils/map-catalog-course-to-embedded-course';

interface RecommendedActivityCardProps {
  activity: RecommendedPlanActivity;
  selectedCourse?: Course | null;
  isChecked: boolean;
  onToggle: (selected?: boolean) => void;
  onSelectCourse: (course: Course) => void;
}

const Icon = ({ isChecked }: { isChecked: boolean }) =>
  isChecked ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />;

export const RecommendedActivityCard = ({
  activity,
  isChecked,
  onToggle,
  selectedCourse,
  onSelectCourse,
}: RecommendedActivityCardProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const isCourseActivity = activity.activity_type === ActivityType.Course;

  const handleClick = () => onToggle();
  const handleRemoveCourse = () => onToggle(false);
  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => setIsDialogOpen(false);

  const mappedSelectedCourse = selectedCourse
    ? mapCatalogCourseToEmbeddedCourse(selectedCourse)
    : null;

  const handleSaveCourse = (course: Course) => {
    onSelectCourse(course);
    onToggle(true);
  };

  return (
    <>
      <ToggleButton
        value={activity.id}
        selected={isChecked}
        onClick={handleClick}
        sx={(theme) => ({
          border: isChecked ? `1px solid ${theme.palette.neutral.darker}` : '',
        })}
      >
        <Stack direction="row" alignItems="flex-start" spacing={2} width="100%">
          <Icon isChecked={isChecked} />
          <Stack sx={{ textAlign: 'left' }} width="100%">
            <Typography fontWeight="bold">{activity.name}</Typography>
            <Typography my={1}>{activity.reason}</Typography>
            {selectedCourse && mappedSelectedCourse ? (
              <SelectedCourseCard
                course={mappedSelectedCourse}
                removable
                onRemove={handleRemoveCourse}
              />
            ) : (
              isCourseActivity && (
                <RecommendedCoursesButton
                  courseType={activity.course_type}
                  onClick={handleOpenDialog}
                />
              )
            )}
          </Stack>
        </Stack>
      </ToggleButton>

      {isDialogOpen && (
        <RecommendedCoursesDialog
          activityId={activity.id}
          onClose={handleCloseDialog}
          onSave={handleSaveCourse}
        />
      )}
    </>
  );
};
